import React from "react";
import {IconButton, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import {DeleteForever, MoreVert, PushPin, PushPinOutlined, Update} from "@mui/icons-material";
import {PlatformTable} from "../PlatformTable";
import {withAppContext} from "../../util/withAppContext";
import withNavigate from "../../util/useNavigate";
import withBreakpoint from "../../util/withBreakpoint";
import {Blue3, Grey7} from "../../theme/styles";
import {classificationByCode} from "../../util/objectClassifications";
import {Grey6, Grey17} from "../../theme/styles";
import ObjectRelationshipFormApplet from "../../eoec/applet/impl/Foundation/ObjectRelationshipFormApplet";
import RecordDrawer from "./RecordDrawer";
import {endpointLabel, resolveEndpoint} from "../../util/relationshipEndpoint";
import {statusPill} from "../../util/statusPill";

class ObjectRelationshipTable extends PlatformTable {

    getCellRenderer(name) {
        if (name === "Related Object Cell") {
            return this.renderRelatedObjectCell;
        }
        if (name === "Action Cell") {
            return this.renderActionCell;
        }
        if (name === "Relating Object Cell") {
            return this.renderRelatingObjectCell;
        }
        if (name === "Related Object Id Cell") {
            return this.renderEndpointIdCell("Child");
        }
        if (name === "Classification Cell") {
            return this.renderClassificationCell;
        }
        if (name === "Relating Object Id Cell") {
            return this.renderEndpointIdCell("Parent");
        }

        return super.getCellRenderer(name);
    }

    // Navigates on ChildRevisionUSID, not ChildUSID: the detail route is keyed on a REVISION, and the
    // edge stores only the logical object. The backend resolves each end to its current revision for
    // exactly this reason, so a row that somehow lacks one renders as plain text rather than as a
    // link that goes nowhere. The route comes from the CHILD's classification, since an edge may span
    // classifications -- a Contract linked to a Supplier must open the Suppliers page.
    // THE OTHER END OF THE SAME EDGE. The "Related to this" grid is queried by ChildUSID, so every
    // row's far end is its PARENT -- and linking on ChildRevisionUSID there would send the reader
    // back to the record they are already on.
    renderRelatingObjectCell = (___, params, __, _) => this.renderEndpointCell(params, "Parent");

    /**
     * The far end's classification by its DISPLAY name -- "Purchase Order", not the code
     * PurchaseOrder the row carries (Ed, 2026-10-28). The code stays the key and is what an
     * unrecognised classification still shows, so nothing goes blank.
     */
    renderClassificationCell = (___, params, columnId, _) => {
        const code = this.state.buffer?.[params?.row?.id]?.get(columnId) ?? params?.row?.[columnId];
        const label = classificationByCode(code)?.singular ?? code ?? "—";
        return <Typography style={{fontSize: 14, color: Grey17}}>{label}</Typography>;
    };

    /**
     * WHICH END THIS TABLE SHOWS. The Related objects applet places the Child columns; Related to
     * this places the Parent ones. The same class draws both, so it reads the answer off what was
     * placed rather than being told twice.
     */
    endShown = () => (this.props.columns ?? []).some(column => column.id === "ParentName") ? "Parent" : "Child";

    /**
     * A ROW CLICK OPENS THE RECORD IN A DRAWER -- its details and form values -- rather than
     * leaving the page (Ed, 2026-10-28: "when clicking on a row in Related Objects and Related to
     * This ... show a right drawer with the object details and form values"). The name's click
     * falls through to the row and opens it too; the drawer's "Full record" link goes to the page.
     */
    onRowClick(params) {
        super.onRowClick(params);
        const resolution = resolveEndpoint(this.state.buffer?.[params?.row?.id] ?? params?.row, this.endShown());
        if (resolution.revisionUSID) this.setState({drawerRevisionUSID: resolution.revisionUSID});
    }

    renderExtension() {
        return (
            <>
                {this.renderRelationshipExtension()}
                {this.state.drawerRevisionUSID ? (
                    <RecordDrawer revisionUSID={this.state.drawerRevisionUSID}
                                  sessionContext={this.props.appContext?.sessionContext}
                                  onClose={() => this.setState({drawerRevisionUSID: null})}/>
                ) : null}
            </>
        );
    }

    renderRelatedObjectCell = (___, params, __, _) => this.renderEndpointCell(params, "Child");

    renderEndpointCell = (params, end) => {
        // The edge may point at a PINNED revision rather than the current one, so what this cell
        // shows -- name, revision, status, and where a click lands -- comes from the resolution,
        // not straight off the row. See util/relationshipEndpoint.
        //
        // FROM THE BUFFER, NOT params.row. A grid row carries ONLY its placed columns, so while
        // Status was a column the row happened to carry ChildStatus and this cell could read it;
        // the day the column went (2026-10-28) every status here went blank, and the revision
        // number never showed at all because nothing ever placed ChildDisplayRevision. The buffer
        // record carries every attribute the EC fetched, which is what the pin menu below reads.
        const record = this.state.buffer?.[params?.row?.id] ?? params?.row;
        const resolution = resolveEndpoint(record, end);

        // Every classification has a page now, so the far end is linkable whenever its code is one we
        // recognise. An unknown code stays plain rather than guessing a route.
        const route = classificationByCode(resolution.classification)?.route;
        const linkable = !!resolution.revisionUSID && !!route;

        // A cell renderer fills the row's full height, so content sits at the TOP unless the wrapper
        // claims that height and centres in it. Both returns go through this: an unlinkable row
        // riding high beside a centred one reads as broken rather than as plain.
        const cell = (content) => (
            <div style={{display: "flex", alignItems: "center", gap: 6, height: "100%", minWidth: 0}}>
                {content}
                {this.renderEndpointStatus(resolution)}
            </div>
        );

        if (!linkable) {
            return cell(<Typography style={{fontSize: 14, color: Grey7}}>{resolution.name || "—"}</Typography>);
        }

        return cell(
            // THE NAME OPENS THE DRAWER TOO. It read as a link and took the reader off the page
            // (Ed, 2026-10-28: "When I click on the name it redirects the page. No right side
            // drawer."). The click falls through to the row, which opens the drawer; the drawer's
            // own "Full record" link is the way to the page.
            <Typography
                style={{fontSize: 14, fontWeight: 500, color: Blue3, cursor: "pointer",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
            >
                {resolution.name || "—"}
            </Typography>
        );
    };

    /**
     * The revision and its status, after the name: "Meridian Purchase Order 9  REV 2  [DRAFT]".
     *
     * Ed, 2026-10-28: "you indicate status in two places. After the Name and in the Status column.
     * Let's change Name to Name <Rev n> <Status>. <Status> should have the properly painted pill
     * and let's remove the Status column." So the Status column is gone from both relationship
     * tables, and this cell carries the whole of it: the revision being shown (the pin when
     * pinned, else the current one) and its status in the app's one pill (util/statusPill), the
     * same paint as every other grid. Until today only a NON-released status drew here, as a
     * small badge, on the theory that silence meant released; with the column gone there is no
     * second place to read RELEASED from, so every status is painted.
     */
    renderEndpointStatus = (resolution) => {
        const rev = resolution.displayRevision;
        return (
            <>
                {rev !== null && rev !== undefined && rev !== "" ? (
                    <Typography style={{fontSize: 12, color: Grey6, flexShrink: 0, whiteSpace: "nowrap"}}>
                        REV {rev}
                    </Typography>
                ) : null}
                {resolution.status ? statusPill(resolution.status) : null}
            </>
        );
    };

    handleMenuClick(identifier) {
        if (identifier === "Add Related Object") {
            this.setState({renderTarget: "Add Related Object"});
            return;
        }
        super.handleMenuClick(identifier);
    }

    /**
     * The dialog for adding, and the one for removing.
     *
     * BOTH ARE THE SAME APPLET. A Delete action has no layout of its own, so PlatformDetail borrows
     * the New one and forces every control disabled -- which is exactly what a confirmation should
     * be: the row you are about to remove, shown, and not editable. Writing a second dialog would
     * be a second place for the two to disagree about what an edge is.
     */
    renderRelationshipExtension() {
        if (this.state.renderTarget === "Add Related Object") {
            return <ObjectRelationshipFormApplet enterpriseComponent={this.applet.getEnterpriseComponent()}
                                                 defaultAction="New"/>;
        }
        if (this.state.renderTarget === "Remove Related Object") {
            return <ObjectRelationshipFormApplet enterpriseComponent={this.applet.getEnterpriseComponent()}
                                                 defaultAction="Delete"/>;
        }
        return super.renderExtension();
    }

    renderActionCell = (instance, params, columnId, _) => {
        if (params.row.id > this.state.buffer.length - 1) return;

        const rowId = params.row.id;
        const open = this.state.actionMenuRowId === rowId && !!this.state.actionMenuAnchor;
        const closeMenu = {actionMenuAnchor: null, actionMenuRowId: null};

        return (
            <div style={{width: "50px", backgroundColor: "#FFFFFF", marginLeft: "-10px"}}>
                <IconButton disableRipple sx={{"&:hover": {backgroundColor: "transparent"}}}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                // The row itself navigates to the related object, so the menu must
                                // not let the click travel.
                                event.stopPropagation();
                                this.applet.getEnterpriseComponent().selectRecord(rowId);
                                this.setState({actionMenuAnchor: event.currentTarget, actionMenuRowId: rowId});
                            }}>
                    <MoreVert sx={{fontSize: "1.35rem", position: "relative", top: "-1px"}}/>
                </IconButton>
                <Menu anchorEl={this.state.actionMenuAnchor}
                      anchorOrigin={{vertical: "center", horizontal: "right"}}
                      transformOrigin={{vertical: "top", horizontal: "left"}}
                      open={open}
                      onClose={() => this.setState(closeMenu)}
                      slotProps={{
                          paper: {elevation: 0, sx: {boxShadow: "none", borderRadius: 0, border: "1px solid #A8A9AF"}},
                          list: {disablePadding: true, sx: {minWidth: "200px", "& .MuiMenuItem-root": {minHeight: "33px", height: "33px"}}},
                          transition: {onExited: () => this.setState(closeMenu)},
                      }}>
                    {/* REMOVE, NOT DELETE. The object at the far end is untouched -- only the edge
                        between them goes -- and "delete" beside an object's name reads like the
                        object itself is at risk. */}
                    {this.renderPinMenuItem(rowId, closeMenu)}
                    <MenuItem key="remove" onClick={() => {
                        this.applet.getEnterpriseComponent().selectRecord(rowId);
                        this.setState({...closeMenu, renderTarget: "Remove Related Object"});
                    }}>
                        <DeleteForever style={{color: Grey6, marginRight: 8, fontSize: 20}}/>
                        <Typography style={{fontSize: "15px", fontWeight: "400", color: Grey17}}>
                            Remove relationship
                        </Typography>
                    </MenuItem>
                </Menu>
            </div>
        );
    };

    /**
     * Pin this link to the revision it currently shows, or let it float again.
     *
     * WITHOUT THIS THE PIN IS A CREATION-TIME DECISION ONLY, and the two things people actually want
     * are both impossible: freezing a link that already exists because the record is about to be
     * revised, and correcting one that was pinned by mistake. The only other route is remove and
     * re-add, which destroys the creation baseline and puts a deletion in the audit trail for what
     * was a change of mind.
     *
     * IT ACTS IMMEDIATELY, with no confirmation, because it is fully reversible by the menu item
     * that replaces it -- the opposite of Remove, which is why that one keeps its dialog.
     *
     * PINS TO WHAT THE ROW IS SHOWING. Not to some other revision chosen elsewhere: the reader is
     * looking at a row that says REV 3, and the only honest thing "Pin" can mean there is REV 3.
     */
    renderPinMenuItem = (rowId, closeMenu) => {
        const resolution = resolveEndpoint(this.state.buffer?.[rowId], "Child");

        // Nothing to pin TO. A row whose child never resolved to a revision would otherwise offer an
        // action that writes null and appears to do nothing.
        if (!resolution.pinned && !resolution.revisionUSID) return null;

        const pinning = !resolution.pinned;
        const label = pinning
            ? (resolution.displayRevision ? `Pin to REV ${resolution.displayRevision}` : "Pin to this revision")
            : "Unpin \u2014 follow the current revision";

        return (
            <MenuItem key="pin" onClick={() => {
                const ec = this.applet.getEnterpriseComponent();
                ec.selectRecord(rowId);
                ec.setAttributeValue("ChildPinnedRevisionUSID", pinning ? resolution.revisionUSID : null);
                ec.executeUpdate();
                this.setState(closeMenu);
            }}>
                {pinning
                    ? <PushPin style={{color: Grey6, marginRight: 8, fontSize: 20, transform: "rotate(45deg)"}}/>
                    : <PushPinOutlined style={{color: Grey6, marginRight: 8, fontSize: 20}}/>}
                <Typography style={{fontSize: "15px", fontWeight: "400", color: Grey17}}>
                    {label}
                </Typography>
            </MenuItem>
        );
    };

    /**
     * "SOP-00018 REV 2" -- what somebody quotes when they name a record -- and whether that revision
     * is the one the edge will still mean tomorrow.
     *
     * The name alone does not identify a record: this realm holds eight documents called "Cleanroom
     * Gowning Work Instruction 11". The revision is part of the identifier, and which revision it is
     * depends on whether the edge is pinned -- which is exactly why the marker belongs HERE, beside
     * the revision it qualifies, rather than in a column of its own that nobody would connect to it.
     *
     * TWO MARKERS, AND THEY MEAN OPPOSITE THINGS:
     *   pin    -- this edge will NOT follow the record forward. It means this revision.
     *   update -- this edge HAS followed the record forward, away from what it was made against.
     * An undrifted floating edge gets neither, because it has nothing to report.
     */
    renderEndpointIdCell = (end) => (___, params, __, _) => {
        const resolution = resolveEndpoint(params?.row, end);
        const text = endpointLabel(resolution);

        const marker = resolution.pinned
            ? <PushPin sx={{fontSize: 13, color: Grey6, flexShrink: 0, transform: "rotate(45deg)"}}/>
            : (resolution.drifted || resolution.pinBroken)
                ? <Update sx={{fontSize: 14, color: Grey6, flexShrink: 0}}/>
                : null;

        const body = (
            <div style={{display: "flex", alignItems: "center", gap: 4, height: "100%", minWidth: 0}}>
                <Typography style={{fontSize: 14, color: Grey17, overflow: "hidden",
                                    textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                    {text || "\u2014"}
                </Typography>
                {marker}
            </div>
        );

        // The sentence only exists when there is something to say, and a Tooltip with no title still
        // changes the cursor -- so an ordinary row must not be wrapped in one at all.
        return resolution.note
            ? <Tooltip title={resolution.note} placement="top">{body}</Tooltip>
            : body;
    };
}

export default withNavigate(withAppContext(withBreakpoint(ObjectRelationshipTable)));
